import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { LayoutDashboard, ArrowRightLeft, Brain, Shield, FileCode, BookOpen } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

export default function Layout({ children }) {
  const location = useLocation();
  const { isAuthenticated, user, sessionId } = useAuth();

  useEffect(() => {
    // Aggressively move Emergent badge to top right corner
    const moveBadgeToTop = () => {
      // Try multiple selectors
      const selectors = [
        'div[style*="position: fixed"][style*="bottom"]',
        'div[style*="position:fixed"][style*="bottom"]',
        'a[href*="emergent"]',
        'div[style*="z-index: 999999"]',
        'div[style*="z-index:999999"]'
      ];
      
      for (const selector of selectors) {
        const elements = document.querySelectorAll(selector);
        elements.forEach(badge => {
          if (badge && badge.style) {
            badge.style.setProperty('bottom', 'auto', 'important');
            badge.style.setProperty('top', '20px', 'important');
            badge.style.setProperty('right', '20px', 'important');
          }
        });
      }
    };
    
    // Run immediately
    moveBadgeToTop();
    
    // Run on interval
    const interval = setInterval(moveBadgeToTop, 100);
    
    // Watch for DOM changes
    const observer = new MutationObserver(moveBadgeToTop);
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['style']
    });
    
    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
    { path: '/training', label: 'Training', icon: Brain, testId: 'nav-training' },
    { path: '/integration', label: 'API', icon: FileCode, testId: 'nav-integration' },
    { path: '/guide', label: 'Guide', icon: BookOpen, testId: 'nav-guide' },
  ];

  const isLandingPage = location.pathname === '/';
  
  // Check for dev mode bypass
  const params = new URLSearchParams(location.search);
  const isDevMode = params.get('dev') === 'true';

  if ((!isAuthenticated && !isDevMode) || isLandingPage) {
    return <div className="min-h-screen bg-white overflow-x-hidden">{children}</div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-20 overflow-x-hidden">
      <div className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="text-blue-600" size={24} strokeWidth={2} />
            <div>
              <h1 className="text-xl font-black tracking-tighter text-zinc-950">
                TRACK CC
              </h1>
              <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-zinc-500">
                {sessionId 
                  ? <span className="text-emerald-600">Live: {sessionId.substring(0, 8)}...</span>
                  : user?.facialId 
                    ? `Session: ${user.facialId.substring(0, 8)}...` 
                    : 'Fraud Detection'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      <main className="pt-20 max-w-7xl mx-auto px-4">{children}</main>

      <nav 
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 z-[100]"
        data-testid="bottom-nav"
      >
        <div className="max-w-7xl mx-auto px-2">
          <div className="flex items-center justify-around py-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={item.testId}
                  className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-zinc-500 hover:text-zinc-900'
                  }`}
                >
                  <Icon size={24} strokeWidth={isActive ? 2.5 : 1.5} />
                  <span className="text-[11px] font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    </div>
  );
}
